'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cx } from '../utils/cx';
import { Icon } from './icon';

type ChatScrollButtonValue = string | number | null | undefined;

export interface ChatScrollButtonProps {
  watch?: ChatScrollButtonValue;
  thresholdPx?: number;
  behavior?: ScrollBehavior;
  scrollContainerId?: string;
  scrollContainerSelector?: string;
  ariaLabel?: string;
  autoScrollWhenNearBottom?: boolean;
  scrollOnMount?: boolean;
  className?: string;
  onScrollToBottom?: () => void;
  onNearBottomChange?: (isNear: boolean) => void;
}

function prefersReducedMotion(): boolean {
  try {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch {
    return false;
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function isElementVisibleForScroll(el: HTMLElement): boolean {
  try {
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    if (el.clientHeight <= 0) return false;

    return true;
  } catch {
    return false;
  }
}

function computeNearBottom(el: HTMLElement, thresholdPx: number): boolean {
  const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
  return distanceFromBottom <= thresholdPx;
}

function findFallbackScrollContainer(): HTMLElement | null {
  if (typeof document === 'undefined') return null;

  const logs = Array.from(document.querySelectorAll<HTMLElement>('[role="log"]'));
  if (logs.length === 1) return logs[0];

  const labeled = logs.filter((el) => String(el.getAttribute('aria-label') ?? '').toLowerCase().includes('chat'));
  if (labeled.length === 1) return labeled[0];

  return null;
}

function getScrollContainer(params: { scrollContainerId?: string; scrollContainerSelector?: string }): HTMLElement | null {
  if (typeof document === 'undefined') return null;

  const byId = String(params.scrollContainerId ?? '').trim();
  if (byId) {
    const el = document.getElementById(byId);
    if (el) return el;

    const fallback = findFallbackScrollContainer();
    if (fallback) return fallback;
  }

  const selector = String(params.scrollContainerSelector ?? '').trim();
  if (selector) {
    try {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el) return el;
    } catch {
      return null;
    }
  }

  return null;
}

function stableHash(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h) ^ input.charCodeAt(i);
  }
  return (h >>> 0).toString(16);
}

function makeWatchKey(value: unknown): string {
  if (Array.isArray(value)) {
    const len = value.length;
    const last = len > 0 ? value[len - 1] : null;

    if (last && typeof last === 'object' && !Array.isArray(last)) {
      const anyLast = last as any;
      const id = String(anyLast.id ?? anyLast.messageId ?? anyLast._id ?? '').trim();
      const createdAt = String(anyLast.createdAt ?? anyLast.created_at ?? '').trim();
      const content = String(anyLast.content ?? '').trim().slice(0, 200);
      const contentSig = content ? stableHash(content) : '';
      return `arr:${len}:${id}:${createdAt}:${contentSig}`;
    }

    return `arr:${len}`;
  }

  try {
    const s = JSON.stringify(value);
    if (s.length > 12_000) return `${s.slice(0, 12_000)}…`;
    return String(value ?? '');
  } catch {
    return String(value ?? '');
  }
}

export function ChatScrollButton(props: ChatScrollButtonProps) {
  const thresholdPx = useMemo(() => {
    const raw = Number(props.thresholdPx ?? 160);
    return Number.isFinite(raw) ? clamp(raw, 40, 800) : 160;
  }, [props.thresholdPx]);

  const scrollOnMount = props.scrollOnMount !== false;
  const autoScrollWhenNearBottom = props.autoScrollWhenNearBottom !== false;

  const ariaLabel =
    typeof props.ariaLabel === 'string' && props.ariaLabel.trim() ? props.ariaLabel.trim() : 'Scroll to latest message';

  const desiredBehavior = String(props.behavior ?? 'smooth').trim().toLowerCase() === 'auto' ? 'auto' : 'smooth';
  const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : (desiredBehavior as ScrollBehavior);

  const watchKey = useMemo(() => makeWatchKey(props.watch), [props.watch]);

  const containerRef = useRef<HTMLElement | null>(null);
  const nearBottomRef = useRef<boolean>(true);
  const [nearBottom, setNearBottom] = useState(true);

  const scrollToBottom = useCallback(
    (b: ScrollBehavior = behavior): boolean => {
      const el = containerRef.current;
      if (!el) return false;

      if (!isElementVisibleForScroll(el)) return false;

      try {
        el.scrollTo({ top: el.scrollHeight, behavior: b });
        return true;
      } catch {
        try {
          el.scrollTop = el.scrollHeight;
          return true;
        } catch {
          return false;
        }
      }
    },
    [behavior],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;
    let cleanup: (() => void) | null = null;

    const start = performance.now();
    const maxRetryMs = 2500;

    const tryResolve = () => {
      if (cancelled) return;

      const params: { scrollContainerId?: string; scrollContainerSelector?: string } = {};
      if (props.scrollContainerId) params.scrollContainerId = props.scrollContainerId;
      if (props.scrollContainerSelector) params.scrollContainerSelector = props.scrollContainerSelector;

      const el = getScrollContainer(params);

      if (!el) {
        if (performance.now() - start < maxRetryMs) window.requestAnimationFrame(tryResolve);
        return;
      }

      containerRef.current = el;

      const update = () => {
        if (!isElementVisibleForScroll(el)) {
          nearBottomRef.current = true;
          setNearBottom(true);
          props.onNearBottomChange?.(true);
          return;
        }

        const isNear = computeNearBottom(el, thresholdPx);
        nearBottomRef.current = isNear;
        setNearBottom(isNear);
        props.onNearBottomChange?.(isNear);
      };

      update();

      el.addEventListener('scroll', update, { passive: true });
      const onResize = () => update();
      window.addEventListener('resize', onResize, { passive: true });

      cleanup = () => {
        el.removeEventListener('scroll', update as any);
        window.removeEventListener('resize', onResize as any);
      };
    };

    tryResolve();

    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, [props.scrollContainerId, props.scrollContainerSelector, thresholdPx, props.onNearBottomChange]);

  const didInitialScrollRef = useRef(false);
  useEffect(() => {
    if (!scrollOnMount) return;
    if (didInitialScrollRef.current) return;
    if (typeof window === 'undefined') return;

    let cancelled = false;

    const start = performance.now();
    const maxRetryMs = 10_000;

    const tick = () => {
      if (cancelled) return;
      if (didInitialScrollRef.current) return;

      const ok = scrollToBottom('auto');
      if (ok) {
        didInitialScrollRef.current = true;
        nearBottomRef.current = true;
        setNearBottom(true);
        return;
      }

      if (performance.now() - start < maxRetryMs) window.requestAnimationFrame(tick);
    };

    window.requestAnimationFrame(tick);

    return () => {
      cancelled = true;
    };
  }, [scrollOnMount, watchKey, scrollToBottom]);

  const didFirstContentScrollRef = useRef(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (!isElementVisibleForScroll(el)) return;

    if (!didFirstContentScrollRef.current) {
      const hasMeaningfulContent = el.scrollHeight > 0 && el.clientHeight > 0;
      if (hasMeaningfulContent) {
        scrollToBottom('auto');
        didFirstContentScrollRef.current = true;
        nearBottomRef.current = true;
        setNearBottom(true);
        return;
      }
    }

    const shouldAuto = autoScrollWhenNearBottom && nearBottomRef.current;
    if (!shouldAuto) return;

    window.requestAnimationFrame(() => scrollToBottom(behavior));
  }, [watchKey, autoScrollWhenNearBottom, behavior, scrollToBottom]);

  const showButton = !nearBottom;

  const buttonClassName = useMemo(() => {
    if (typeof props.className === 'string' && props.className.trim()) return props.className.trim();

    return cx(
      'absolute left-1/2 -translate-x-1/2 bottom-5',
      'inline-flex h-10 w-10 items-center justify-center rounded-full',
      'border border-gray-200 bg-white/90 shadow-sm backdrop-blur-sm',
      'text-gray-800 hover:bg-white',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white',
      'dark:border-gray-800 dark:bg-gray-950/80 dark:text-gray-100 dark:hover:bg-gray-950',
      'dark:focus-visible:ring-offset-gray-950',
    );
  }, [props.className]);

  if (!showButton) return null;

  return (
    <button
      type="button"
      onClick={() => {
        scrollToBottom('smooth');
        props.onScrollToBottom?.();
      }}
      className={buttonClassName}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      <Icon name="chevron-down" size={20} strokeWidth={2} className="text-current" />
    </button>
  );
}

ChatScrollButton.displayName = 'ChatScrollButton';
